// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { stripBom } from './framing';

/**
 * RFC 4180 CSV/TSV reader.
 *
 * Hand-written rather than imported because the requirements are small and exact: quoted commas,
 * embedded newlines inside quotes, doubled quotes, CRLF, BOM, and ragged rows that must not throw.
 * An eval export is frequently ragged — a trailing comment column present on some rows only — and
 * a parser that throws on that loses the whole file.
 */
export function parseCsv(rawText: string, delimiter?: string): string[][] {
  const text = stripBom(rawText);
  const delim = delimiter ?? sniffDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  const endField = (): void => {
    row.push(field);
    field = '';
  };
  const endRow = (): void => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"' && field.length === 0) {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === delim) {
      endField();
      i++;
      continue;
    }
    if (ch === '\r') {
      // CRLF and a lone CR both terminate a row.
      endRow();
      i += text[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (ch === '\n') {
      endRow();
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  if (field.length > 0 || row.length > 0) endRow();

  // A trailing newline produces one empty row; drop it rather than emitting a blank record.
  while (rows.length > 0) {
    const last = rows[rows.length - 1]!;
    if (last.length === 1 && last[0] === '') rows.pop();
    else break;
  }
  return rows;
}

function sniffDelimiter(text: string): string {
  const head = text.slice(0, 4096);
  const firstLine = head.split(/\r?\n/, 1)[0] ?? '';
  const tabs = (firstLine.match(/\t/g) ?? []).length;
  const commas = (firstLine.match(/,/g) ?? []).length;
  const semis = (firstLine.match(/;/g) ?? []).length;
  if (tabs > commas && tabs > semis) return '\t';
  if (semis > commas) return ';';
  return ',';
}

/** Header row + object rows, with duplicate headers disambiguated so no column is silently lost. */
export function csvToObjects(rawText: string): { headers: string[]; rows: Record<string, string>[] } {
  const table = parseCsv(rawText);
  if (table.length === 0) return { headers: [], rows: [] };
  const seen = new Map<string, number>();
  const headers = (table[0] ?? []).map((h, i) => {
    const base = h.trim() === '' ? `column_${i + 1}` : h.trim();
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}_${n + 1}`;
  });
  const rows: Record<string, string>[] = [];
  for (let r = 1; r < table.length; r++) {
    const cells = table[r] ?? [];
    const obj: Record<string, string> = {};
    for (let c = 0; c < headers.length; c++) obj[headers[c]!] = cells[c] ?? '';
    rows.push(obj);
  }
  return { headers, rows };
}

const NEEDS_QUOTE = /[",;\r\n\t]/;
/**
 * A cell beginning with any of these is executed by Excel, LibreOffice and Sheets. `regression.csv`
 * carries raw model output and validator messages that quote instance values, and it is explicitly
 * targeted at Excel via the BOM — so this guard is load-bearing, not theoretical.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

export function csvCell(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  let s = String(value);
  // One finding is one physical line. A findings file whose row count differs from its line count
  // cannot be wc -l'd or grep'd, so control characters become visible escapes.
  s = s.replace(/\r\n|\r|\n/g, '\\n').replace(/\t/g, '\\t');
  if (FORMULA_LEAD.test(s)) s = `'${s}`;
  if (NEEDS_QUOTE.test(s) || s !== s.trim()) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function csvRow(values: readonly (string | number | boolean | null | undefined)[]): string {
  return values.map(csvCell).join(',') + '\r\n';
}
